import React, { useMemo, useState } from 'react';
import Button from '@/components/UI/Button';
import Card from '@/components/UI/Card';
import FormNumberInput from '@/components/UI/FormNumberInput';
import FormSelect from '@/components/UI/FormSelect';
import FormSwitch from '@/components/UI/FormSwitch';
import { trpc } from '@/api/trpc';
import { useQueryClient } from '@tanstack/react-query';
import { useAppQuery } from '@/hooks/useAppQuery';
import { useTrpcMutation } from '@/api/useTrpcMutation';
import type {
    GovernanceCapReachedMode,
    GovernanceEntityTypeScope,
    GovernanceContentionMode,
    GovernanceExposureKeyGrain,
    GovernanceOppositeDirectionMode,
    GovernanceSameDirectionMode,
    GovernanceSelectionMetric,
    TradingStrategyForPortfolio
} from '@einhaus/project314-types';
import GovernanceEntityLimitModal from './GovernanceEntityLimitModal';
import GovernanceEntityLimitsTable from './GovernanceEntityLimitsTable';
import GovernanceFamilyLimitModal from './GovernanceFamilyLimitModal';
import GovernanceFamilyLimitsTable, { type GovernanceFamilyOption } from './GovernanceFamilyLimitsTable';
import GovernanceStrategyLimitModal from './GovernanceStrategyLimitModal';
import GovernanceStrategyLimitsTable, { type GovernanceStrategyOption } from './GovernanceStrategyLimitsTable';
import GovernanceSubscriptionPriorityTable from './GovernanceSubscriptionPriorityTable';
import { useGovernanceDraftState } from './useGovernanceDraftState';
import {
    CAP_REACHED_OPTIONS,
    CONTENTION_OPTIONS,
    DEFAULT_SUBSCRIPTION_PRIORITY,
    EXPOSURE_GRAIN_OPTIONS,
    GOVERNANCE_POLICY_NAME,
    type GovernanceEntityLimitDraft,
    type GovernanceFamilyLimitDraft,
    type GovernanceStrategyLimitDraft,
    OPPOSITE_DIRECTION_OPTIONS,
    SAME_DIRECTION_OPTIONS,
    SELECTION_METRIC_OPTIONS,
    usesReplacement
} from './governanceHelpers';

interface PortfolioGovernanceCardProps {
    portfolioId: string | undefined;
    strategiesSubscribed: TradingStrategyForPortfolio[] | null | undefined;
}

const PortfolioGovernanceCard: React.FC<PortfolioGovernanceCardProps> = ({ portfolioId, strategiesSubscribed }) => {
    const queryClient = useQueryClient();
    const numericPortfolioId = portfolioId ? parseInt(portfolioId) : 0;

    // Subscribed strategies that can carry a ranking priority (a priority row needs a linked trading profile).
    const linkedStrategies = useMemo(
        () =>
            (strategiesSubscribed ?? [])
                .filter((strategy) => strategy.tradingProfileId > 0)
                .map((strategy) => ({
                    strategyId: strategy.id,
                    tradingProfileId: strategy.tradingProfileId,
                    name: strategy.name,
                    isActive: strategy.isActive
                })),
        [strategiesSubscribed]
    );

    const policyQuery = useAppQuery(
        {
            ...trpc.portfolioGovernance.getPolicy.queryOptions({ portfolioId: numericPortfolioId }),
            enabled: numericPortfolioId > 0
        },
        { fallbackErrorMessage: 'Failed to load governance policy' }
    );

    const activePolicy = policyQuery.data?.policy ?? null;

    // Editable draft + enabled flag, kept in sync with the loaded policy (see hook for the sync semantics).
    const { isEnabled, setIsEnabled, draft, setDraft } = useGovernanceDraftState(policyQuery.isSuccess, activePolicy);

    const familiesQuery = useAppQuery(
        {
            ...trpc.strategyVariant.listFamilies.queryOptions(),
            enabled: isEnabled
        },
        { fallbackErrorMessage: 'Failed to load strategy families' }
    );

    const familyOptions = useMemo<GovernanceFamilyOption[]>(
        () =>
            (familiesQuery.data?.families ?? []).map(({ family }) => ({
                familyId: family.id,
                name: family.name
            })),
        [familiesQuery.data?.families]
    );

    const strategyOptions = useMemo<GovernanceStrategyOption[]>(
        () => linkedStrategies.map((strategy) => ({ strategyId: strategy.strategyId, name: strategy.name })),
        [linkedStrategies]
    );

    const [limitModalOpen, setLimitModalOpen] = useState(false);
    const [editingScope, setEditingScope] = useState<GovernanceEntityTypeScope | null>(null);
    const [familyLimitModalOpen, setFamilyLimitModalOpen] = useState(false);
    const [editingFamilyId, setEditingFamilyId] = useState<number | null>(null);
    const [strategyLimitModalOpen, setStrategyLimitModalOpen] = useState(false);
    const [editingStrategyId, setEditingStrategyId] = useState<number | null>(null);

    const invalidatePolicy = () =>
        queryClient.invalidateQueries({
            queryKey: trpc.portfolioGovernance.getPolicy.queryOptions({ portfolioId: numericPortfolioId }).queryKey
        });

    const saveMutation = useTrpcMutation(trpc.portfolioGovernance.savePolicy.mutationOptions({ onSuccess: invalidatePolicy }), {
        successMessage: 'Governance policy saved',
        fallbackErrorMessage: 'Failed to save governance policy'
    });

    const deactivateMutation = useTrpcMutation(trpc.portfolioGovernance.deactivate.mutationOptions({ onSuccess: invalidatePolicy }), {
        successMessage: 'Position governance disabled',
        fallbackErrorMessage: 'Failed to disable governance'
    });

    const isBusy = saveMutation.isPending || deactivateMutation.isPending;

    const handleToggleEnabled = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const next = event.target.checked;
        setIsEnabled(next);

        // Turning governance off only requires a server call when an active policy currently exists.
        if (!next && activePolicy) {
            await deactivateMutation.mutateAsync({ portfolioId: numericPortfolioId });
        }
    };

    const handleSave = async () => {
        if (numericPortfolioId <= 0) {
            return;
        }

        await saveMutation.mutateAsync({
            portfolioId: numericPortfolioId,
            name: GOVERNANCE_POLICY_NAME,
            exposureKeyGrain: draft.exposureKeyGrain,
            sameDirectionAdmissionMode: draft.sameDirectionAdmissionMode,
            oppositeDirectionMode: draft.oppositeDirectionMode,
            capReachedMode: draft.capReachedMode,
            contentionMode: draft.contentionMode,
            selectionMetric: draft.selectionMetric,
            requiresSharedScorecard: draft.requiresSharedScorecard,
            contentionWindowMs: draft.contentionWindowMs,
            replacementThresholdPercent: draft.replacementThresholdPercent,
            entityLimits: draft.entityLimits,
            familyLimits: draft.familyLimits,
            strategyLimits: draft.strategyLimits,
            subscriptionPriorities: linkedStrategies.map((strategy) => ({
                strategyId: strategy.strategyId,
                tradingProfileId: strategy.tradingProfileId,
                priority: draft.subscriptionPriorities[strategy.strategyId] ?? DEFAULT_SUBSCRIPTION_PRIORITY
            }))
        });
    };

    const handleSetPriority = (strategyId: number, priority: number) =>
        setDraft((prev) => ({ ...prev, subscriptionPriorities: { ...prev.subscriptionPriorities, [strategyId]: priority } }));

    const editingLimit =
        editingScope === null ? null : (draft.entityLimits.find((limit) => limit.entityTypeScope === editingScope) ?? null);

    const editingFamilyLimit =
        editingFamilyId === null ? null : (draft.familyLimits.find((limit) => limit.familyId === editingFamilyId) ?? null);

    const editingStrategyLimit =
        editingStrategyId === null ? null : (draft.strategyLimits.find((limit) => limit.strategyId === editingStrategyId) ?? null);

    const usedScopes = draft.entityLimits.map((limit) => limit.entityTypeScope);
    const usedFamilyIds = draft.familyLimits.map((limit) => limit.familyId);
    const usedStrategyIds = draft.strategyLimits.map((limit) => limit.strategyId);

    const handleSaveLimit = (limit: GovernanceEntityLimitDraft) => {
        setDraft((prev) => {
            const exists = prev.entityLimits.some((entry) => entry.entityTypeScope === limit.entityTypeScope);

            const entityLimits = exists
                ? prev.entityLimits.map((entry) => (entry.entityTypeScope === limit.entityTypeScope ? limit : entry))
                : [...prev.entityLimits, limit];

            return { ...prev, entityLimits };
        });
    };

    const handleDeleteLimit = (scope: GovernanceEntityTypeScope) =>
        setDraft((prev) => ({ ...prev, entityLimits: prev.entityLimits.filter((entry) => entry.entityTypeScope !== scope) }));

    const handleSaveFamilyLimit = (limit: GovernanceFamilyLimitDraft) => {
        setDraft((prev) => {
            const exists = prev.familyLimits.some((entry) => entry.familyId === limit.familyId);

            const familyLimits = exists
                ? prev.familyLimits.map((entry) => (entry.familyId === limit.familyId ? limit : entry))
                : [...prev.familyLimits, limit];

            return { ...prev, familyLimits };
        });
    };

    const handleDeleteFamilyLimit = (familyId: number) =>
        setDraft((prev) => ({ ...prev, familyLimits: prev.familyLimits.filter((entry) => entry.familyId !== familyId) }));

    const handleSaveStrategyLimit = (limit: GovernanceStrategyLimitDraft) => {
        setDraft((prev) => {
            const exists = prev.strategyLimits.some((entry) => entry.strategyId === limit.strategyId);
            const strategyLimits = exists
                ? prev.strategyLimits.map((entry) => (entry.strategyId === limit.strategyId ? limit : entry))
                : [...prev.strategyLimits, limit];

            return { ...prev, strategyLimits };
        });
    };

    const handleDeleteStrategyLimit = (strategyId: number) =>
        setDraft((prev) => ({ ...prev, strategyLimits: prev.strategyLimits.filter((entry) => entry.strategyId !== strategyId) }));

    const openAddLimit = () => {
        setEditingScope(null);
        setLimitModalOpen(true);
    };

    const openEditLimit = (scope: GovernanceEntityTypeScope) => {
        setEditingScope(scope);
        setLimitModalOpen(true);
    };

    const openAddFamilyLimit = () => {
        setEditingFamilyId(null);
        setFamilyLimitModalOpen(true);
    };

    const openEditFamilyLimit = (familyId: number) => {
        setEditingFamilyId(familyId);
        setFamilyLimitModalOpen(true);
    };

    const openAddStrategyLimit = () => {
        setEditingStrategyId(null);
        setStrategyLimitModalOpen(true);
    };

    const openEditStrategyLimit = (strategyId: number) => {
        setEditingStrategyId(strategyId);
        setStrategyLimitModalOpen(true);
    };

    return (
        <Card
            className="mb-4"
            headerClassName="flex justify-between items-center"
            subtitle="Coordinate how multiple subscribed strategies open positions on the same entity."
            title="Position Governance"
        >
            <FormSwitch
                checked={isEnabled}
                disabled={isBusy || policyQuery.isPending || numericPortfolioId <= 0}
                helpText="When off, every subscribed strategy trades independently with no cross-strategy coordination."
                id="governanceEnabled"
                label="Govern multi-strategy positions"
                onChange={handleToggleEnabled}
            />

            {isEnabled && (
                <div className="mt-4 space-y-4">
                    <FormSelect
                        helpText="What counts as 'the same position' when comparing trades across strategies."
                        id="governanceExposureGrain"
                        label="Exposure grain"
                        options={EXPOSURE_GRAIN_OPTIONS}
                        value={draft.exposureKeyGrain}
                        onChange={(e) => setDraft({ ...draft, exposureKeyGrain: e.target.value as GovernanceExposureKeyGrain })}
                    />

                    <FormSelect
                        helpText="How to treat a second strategy entering the same direction on an entity already held."
                        id="governanceSameDirection"
                        label="Same-direction duplicates"
                        options={SAME_DIRECTION_OPTIONS}
                        value={draft.sameDirectionAdmissionMode}
                        onChange={(e) => setDraft({ ...draft, sameDirectionAdmissionMode: e.target.value as GovernanceSameDirectionMode })}
                    />

                    <FormSelect
                        helpText="How to treat a strategy entering the opposite direction of an existing position. Replacement modes run as diagnostics in backtests; live routes the entry for manual approval and closes the conflicting trade(s) first on approval."
                        id="governanceOppositeDirection"
                        label="Opposite-direction conflicts"
                        options={OPPOSITE_DIRECTION_OPTIONS}
                        value={draft.oppositeDirectionMode}
                        onChange={(e) => setDraft({ ...draft, oppositeDirectionMode: e.target.value as GovernanceOppositeDirectionMode })}
                    />

                    <FormSelect
                        helpText="What to do when a per-entity position cap is reached. Replace-weakest evicts the lowest-ranked position for a stronger candidate (backtest diagnostic; live routes for manual approval and closes the weakest first on approval)."
                        id="governanceCapReached"
                        label="When a per-entity cap is reached"
                        options={CAP_REACHED_OPTIONS}
                        value={draft.capReachedMode}
                        onChange={(e) => setDraft({ ...draft, capReachedMode: e.target.value as GovernanceCapReachedMode })}
                    />

                    {usesReplacement(draft) && (
                        <FormNumberInput
                            helpText="Minimum improvement a candidate must clear over the position it would replace, before any churn. Blank = any improvement."
                            id="governanceReplacementThreshold"
                            label="Replacement improvement threshold"
                            min={0}
                            placeholder="Any improvement"
                            step={0.1}
                            suffix="%"
                            value={draft.replacementThresholdPercent}
                            onValueChange={(value) => setDraft({ ...draft, replacementThresholdPercent: value })}
                        />
                    )}

                    <FormSelect
                        helpText="When several strategies signal the same entity at once, keep one winner or admit all that fit."
                        id="governanceContention"
                        label="Simultaneous contention"
                        options={CONTENTION_OPTIONS}
                        value={draft.contentionMode}
                        onChange={(e) => setDraft({ ...draft, contentionMode: e.target.value as GovernanceContentionMode })}
                    />

                    <FormSelect
                        helpText="Which metric ranks competing candidates to pick the winner."
                        id="governanceSelectionMetric"
                        label="Winner selection metric"
                        options={SELECTION_METRIC_OPTIONS}
                        value={draft.selectionMetric}
                        onChange={(e) => setDraft({ ...draft, selectionMetric: e.target.value as GovernanceSelectionMetric })}
                    />

                    <FormSwitch
                        checked={draft.requiresSharedScorecard}
                        helpText="Only compare predicted profit across strategies that share the same scorecard, so the numbers are comparable."
                        id="governanceRequiresSharedScorecard"
                        label="Require a shared scorecard to compare predicted profit"
                        onChange={(e) => setDraft({ ...draft, requiresSharedScorecard: e.target.checked })}
                    />

                    <FormNumberInput
                        integer
                        helpText="Optional window for grouping near-simultaneous entries before ranking. Blank = react per entry."
                        id="governanceContentionWindow"
                        label="Contention window"
                        min={0}
                        placeholder="Per entry"
                        step={100}
                        suffix="ms"
                        value={draft.contentionWindowMs}
                        onValueChange={(value) => setDraft({ ...draft, contentionWindowMs: value })}
                    />

                    <GovernanceEntityLimitsTable
                        entityLimits={draft.entityLimits}
                        onAdd={openAddLimit}
                        onDelete={handleDeleteLimit}
                        onEdit={openEditLimit}
                    />

                    <GovernanceFamilyLimitsTable
                        families={familyOptions}
                        familyLimits={draft.familyLimits}
                        onAdd={openAddFamilyLimit}
                        onDelete={handleDeleteFamilyLimit}
                        onEdit={openEditFamilyLimit}
                    />

                    <GovernanceStrategyLimitsTable
                        strategies={strategyOptions}
                        strategyLimits={draft.strategyLimits}
                        onAdd={openAddStrategyLimit}
                        onDelete={handleDeleteStrategyLimit}
                        onEdit={openEditStrategyLimit}
                    />

                    {linkedStrategies.length > 0 && (
                        <GovernanceSubscriptionPriorityTable
                            priorities={draft.subscriptionPriorities}
                            strategies={linkedStrategies}
                            onChange={handleSetPriority}
                        />
                    )}

                    <div className="flex justify-end border-t border-zinc-800 pt-4">
                        <Button disabled={isBusy} isLoading={saveMutation.isPending} variant="primary" onClick={handleSave}>
                            {activePolicy ? 'Save changes' : 'Enable governance'}
                        </Button>
                    </div>
                </div>
            )}

            <GovernanceEntityLimitModal
                existing={editingLimit}
                isOpen={limitModalOpen}
                usedScopes={usedScopes}
                onClose={() => setLimitModalOpen(false)}
                onSave={handleSaveLimit}
            />
            <GovernanceFamilyLimitModal
                existing={editingFamilyLimit}
                families={familyOptions}
                isOpen={familyLimitModalOpen}
                usedFamilyIds={usedFamilyIds}
                onClose={() => setFamilyLimitModalOpen(false)}
                onSave={handleSaveFamilyLimit}
            />
            <GovernanceStrategyLimitModal
                existing={editingStrategyLimit}
                isOpen={strategyLimitModalOpen}
                strategies={strategyOptions}
                usedStrategyIds={usedStrategyIds}
                onClose={() => setStrategyLimitModalOpen(false)}
                onSave={handleSaveStrategyLimit}
            />
        </Card>
    );
};

export default PortfolioGovernanceCard;
